import { HttpClient, HttpHeaders } from "@angular/common/http";
import { computed, inject, Injectable, signal } from "@angular/core";
import { Config, HttpClient as ForgerockHttpClient, FRUser, TokenManager } from '@forgerock/javascript-sdk';
import { UserService } from "./user.service";
import { firstValueFrom, type Observable } from "rxjs";
import { environment } from "../../environments/environment";
import { UserDetails } from "../models/user";
import { PendoService } from '@/app/services/pendo.service';
import { hashKey } from "../utils/hash-key-utils";

type LoginType = "embedded" | "redirect" | undefined;

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  
  private http = inject(HttpClient);
  private userService = inject(UserService);
  private pendoService = inject(PendoService);
  private readonly clientId = 'CloudPayUI'
  private readonly validationDelay = 60000;

  private accessToken = signal<string | null>(null);
  private refreshToken = signal<string | null>(null);
  private idToken = signal<string | null>(null);

  public userTokens = computed(() => {
    return {
      accessToken: this.accessToken(),
      refreshToken: this.refreshToken(),
      idToken: this.idToken()
    };
  })
  
  public bootstrapAuthClient() {
    Config.set({
      clientId: this.clientId,
      redirectUri: `${window.location.origin}/callback`, // where to redirect after login
      scope: 'openid fr:idm:* apps',
      serverConfig: {
        baseUrl: `${environment.authUrl}/am`,
        timeout: 3000,
      },
      realmPath: 'alpha',
    });
  }

  /**
  * Validates the user's session and fetches user details if necessary.
  * @param renew - Whether to renew the tokens if they are not valid.
  * @returns A Promise that resolves to true if the token is valid, false otherwise.
  */
  public async validateUserSession(renew: boolean): Promise<boolean> {
    const isAuthenticated = this.userService.isAuthenticated();
    const lastValidated = this.userService.currentUser().lastValidated;
    
    try {
      const tokensValid = await this.validateTokens(renew, 'redirect', false);
      
      if (!tokensValid) return false;
      
      if (isAuthenticated && ((Date.now() - new Date(lastValidated).getTime()) <= this.validationDelay)) return true;
      const headers = {
        'Accept-API-Version': 'protocol=1.0,resource=2.0'
      }
      const { valid, uid } = await firstValueFrom(this.accessManagementRequest('sessions', 'POST',  headers, {_action: 'validate'}));
      
      if (valid) {
        if(!isAuthenticated && uid){
          const userDetails = await this.loadUserData(uid);
          this.setUserData(userDetails);
        }
        return true;
      } 
      else {
        console.warn('Invalid Session');
        return false;
      }
    } catch (error) {
      console.error('Error validating session', error);
      throw error;
    }
  }
  
  /**
  * Sends an HTTP request to the Access Management API.
  * @param resource - Path of the AM API resource.
  * @param queryParams - Query parameters
  * @param method - HTTP method to use.
  *
  */
  public accessManagementRequest(resource: string, method: string, headers: {}, params?: {}, body?: {}) {
    const url = `${environment.authUrl}/am/json/${resource}`;
    
    const httpHeaders = new HttpHeaders({
      'Accept': 'application/json, text/plain, */*',
      ...headers,
    });
  
    return this.http.request(method, url, {
      headers: httpHeaders,
      params,
      withCredentials: true,
      body,
    }) as Observable<Record<string, any>>;
    
  }

  /**
  * Validates the user's tokens by checking if they are still valid and optionally renewing them.
  * @param renew - Whether to renew the tokens if they are not valid.
  * @param login - Specifies the type of login: 'embedded' or 'redirect' or undefined.
  * @param skipBackgroundRequest - If true, skips the background request to get tokens without redirect
  * @returns A Promise that resolves to true if the tokens are valid, false otherwise.
  */
  public async validateTokens(renew: boolean, login: LoginType, skipBackgroundRequest: boolean): Promise<boolean> {
    try {
      const tokens = await TokenManager.getTokens({
        forceRenew: renew,
        login,
        skipBackgroundRequest
      });

      if (!tokens) return false;
      this.accessToken.set(tokens.accessToken || null);
      this.refreshToken.set(tokens.refreshToken || null);
      this.idToken.set(tokens.idToken || null);

      return !!(tokens && tokens.accessToken && tokens.idToken);
    } catch (err) {
      return false;
    }
  }
  
  /**
  * Makes an authenticated request to the ForgeRock Identity Management API.
  * @param resource - The resource path to access in the IDM API (e.g., 'managed/user', 'managed/alpha_user/{userId}').
  * @param method - The HTTP method to use for the request (e.g., 'GET', 'POST', 'PUT', 'DELETE').
  * @param data - Optional data to send with the request, typically in JSON format.
  * @returns - A Promise that resolves to the response data from the IDM API, or throws an error if the request fails.
  */
  public async identityManagementRequest(resource:string, method:string, data?: any) {
    let result = null;
    try {
      if (method !== 'GET') await this.validateUserSession(false);
      
      const response = await ForgerockHttpClient.request({
        url: `${environment.authUrl}/openidm/${resource}`,
        timeout: 10000,
        bypassAuthentication: false,
        requiresNewToken: () => false,
        init: {
          body: data && JSON.stringify(data),
          headers: {
            'Content-Type': 'application/json',
          },
          method,
        },
      });
      if (!response.ok) throw new Error(response.statusText);
      result = await response.json();
    } catch (err) {
      console.error(`Error in Forgerock API request for ${resource}`, err);
      throw err;
    }
    return result;
  }
  
  /**
  * Retrieves user details from ForgeRock IDM (Identity Management) system by user ID.
  * @param userId - The unique identifier of the user to retrieve.
  * @returns A Promise that resolves to the user data if successful, or throws an error if the request fails or returns invalid data.
  */
   private async loadUserData(userId: string) {
    if (!userId) {
      return false;
    }
    try {
      const json = await this.identityManagementRequest(`managed/alpha_user/${userId}`, 'GET');
      if (!json || json.error || !json.userName) throw new Error(`Invalid response data: ${json}`);
      return json;
    } catch (err) {
      throw new Error(`Unable to retrieve user details due to "${err}"`);
    }
  }
  
  /**
  * Sets the current user state with user details and their assigned applications.
  * @param userDetails - An object containing user information.
  */
  private async setUserData(userDetails: UserDetails): Promise<void> {
    const isFederated = userDetails.custom_org_federated.length > 0
      ? userDetails.custom_org_federated[0]
      : false

    let userDigest!: string;
    if (userDetails.mail) {
      userDigest = await hashKey(userDetails.mail);
    }

    this.userService.setUserData({
      userId: userDetails._id,
      username: userDetails.userName,
      givenName: userDetails.givenName,
      surname: userDetails.sn,
      email: userDetails.mail,
      assignedApps: userDetails.effectiveApplications,
      orgType: userDetails.frIndexedString2,
      lastValidated: new Date(),
      isFederated,
      digest: userDigest,
      kbaInfo: userDetails.kbaInfo
    });

    this.sendToPendo();
  }
  
  /**
  * Handles the authentication callback from the identity provider.
  *
  * @param code Challenge code (PKCE) returned by the identity provider after user authentication.
  * @param state To protect against CSRF attacks.
  */
  public async handleAuthCallback(code: string, state:string) {
    let tries = 0;
    while (tries < 2) {
      try {
        const tokens = await TokenManager.getTokens({query: {code, state}});
        if (!tokens) throw new Error('No access token returned');
        return true;
      } catch (error) {
        console.warn(`Cant get access token after ${tries} attempt(s) ${error}`);
        tries++;
      }
      await new Promise((r) => setTimeout(r, 1000));
    }
    console.info(`Failed to get access token after ${tries} attempts, trying to validate tokens`);
    return this.validateTokens(false, "redirect", false);
  }
  
  public async logout() {
    this.userService.setUserData({});
    await FRUser.logout();
  }

  public async testSession() {
    let minutesToExpiry;
    const headers = {
      'Accept-API-Version': 'protocol=1.0,resource=2.0'
    }
    try {
      const { maxIdleExpirationTime }  = await firstValueFrom(this.accessManagementRequest('sessions', 'POST', headers, {_action: 'getSessionInfo'}));
      const expiry = Date.parse(maxIdleExpirationTime);
      // Use the cieling value - once the session is actually expired we will be getting auth errors anyway
      minutesToExpiry = Math.ceil((expiry - Date.now()) / (1000 * 60));
    } catch (err: any) {
      const { error } = err;
      if ( error.status !== 401){
        throw err;
      }
      else {
        minutesToExpiry = 0;
      }
    }
    return minutesToExpiry;
  }

  /**
   * Initializes the Pendo tracking script with the current user's session data.
   *
   * This method gathers the visitor and account information from the current authenticated user and sends it to Pendo
   * using the configured API key.
   *
   * If Pendo has already been initialized during the session,
   * duplicate initialization will be skipped within `PendoService`.
   */
  public sendToPendo(): void {
    if (!environment.isPendoEnabled) return;

    const user = this.userService.currentUser();

    const visitor = {
      id: user.username,
      email: user.email,
      full_name: this.userService.fullname,
      role: (user.orgType ?? '').toUpperCase(),
    };

    const account = {
      id: user.orgId,
      name: user.orgName,
      type: (user.orgType ?? '').toUpperCase(),
    };

    this.pendoService.initialize(visitor, account);
  }
}
